import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useStoreAuth } from '../../contexts/StoreAuthContext';
import StoreHeader from '../../components/Layout/StoreHeader';
import { storePartnerAPI } from '../../services/storeAPI';

interface APIKey {
  id: number;
  name: string;
  key_type: 'live' | 'test';
  masked_key: string;
  is_active: boolean;
  permissions: Record<string, boolean>;
  rate_limit_per_minute: number;
  ip_allowlist: string[] | null;
  last_used_at: string | null;
  request_count: number;
  created_at: string;
  created_by_name: string | null;
}

interface WebhookConfig {
  id: number;
  webhook_url: string;
  events: string[];
  is_active: boolean;
  last_triggered_at: string | null;
  failure_count: number;
  created_at: string;
}

interface PurchaseSync {
  id: number;
  partner_transaction_id: string;
  purchase_date: string;
  purchase_time: string | null;
  items: Array<{ product: string; qty: number; price?: number }>;
  total: string;
  loyalty_credited: number;
  free_bag_earned: boolean;
  bins_updated: string[];
  status: string;
  error_message: string | null;
  processed_at: string;
  member_name: string | null;
}

const WEBHOOK_EVENTS = [
  { value: 'reward_earned', label: 'Reward Earned', description: 'When a customer earns a free bag' },
  { value: 'reward_redeemed', label: 'Reward Redeemed', description: 'When a free bag is redeemed' },
  { value: 'bin_low', label: 'Bin Low Stock', description: 'When a bulk bin drops below threshold' },
  { value: 'bin_critical', label: 'Bin Critical', description: 'When a bin is nearly empty' },
  { value: 'bin_empty', label: 'Bin Empty', description: 'When a bin is completely empty' },
  { value: 'member_linked', label: 'Member Linked', description: 'When a customer links their account' },
];

const StoreAPIIntegration: React.FC = () => {
  const { user, isAuthenticated } = useStoreAuth();
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState<'keys' | 'webhooks' | 'logs'>('keys');
  const [apiKeys, setAPIKeys] = useState<APIKey[]>([]);
  const [webhooks, setWebhooks] = useState<WebhookConfig[]>([]);
  const [purchaseLogs, setPurchaseLogs] = useState<PurchaseSync[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  // Create key modal state
  const [showCreateKeyModal, setShowCreateKeyModal] = useState(false);
  const [newKeyName, setNewKeyName] = useState('');
  const [newKeyType, setNewKeyType] = useState<'live' | 'test'>('test');
  const [createdKey, setCreatedKey] = useState<string | null>(null);
  const [createKeyLoading, setCreateKeyLoading] = useState(false);

  // Create webhook modal state
  const [showWebhookModal, setShowWebhookModal] = useState(false);
  const [webhookUrl, setWebhookUrl] = useState('');
  const [webhookEvents, setWebhookEvents] = useState<string[]>([]);
  const [createWebhookLoading, setCreateWebhookLoading] = useState(false);

  const loadData = useCallback(async () => {
    try {
      setLoading(true);
      const [keysData, webhooksData, logsData] = await Promise.all([
        storePartnerAPI.getAPIKeys(),
        storePartnerAPI.getWebhooks(),
        storePartnerAPI.getPurchaseSyncLog(),
      ]);
      setAPIKeys(keysData.keys || []);
      setWebhooks(webhooksData.webhooks || []);
      setPurchaseLogs(logsData.purchases || []);
      setError('');
    } catch (err: any) {
      setError(err.message || 'Failed to load data');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (!isAuthenticated) {
      navigate('/store/login');
      return;
    }
    loadData();
  }, [isAuthenticated, navigate, loadData]);

  const handleCreateKey = async () => {
    if (!newKeyName.trim()) {
      alert('Please enter a name for the API key');
      return;
    }

    try {
      setCreateKeyLoading(true);
      const response = await storePartnerAPI.createAPIKey({
        name: newKeyName,
        key_type: newKeyType,
      });
      setCreatedKey(response.api_key);
      setAPIKeys([response.key_details, ...apiKeys]);
      setNewKeyName('');
    } catch (err: any) {
      alert(err.message || 'Failed to create API key');
    } finally {
      setCreateKeyLoading(false);
    }
  };

  const handleRevokeKey = async (keyId: number) => {
    if (!window.confirm('Are you sure you want to revoke this API key? This action cannot be undone.')) {
      return;
    }

    try {
      await storePartnerAPI.revokeAPIKey(keyId);
      setAPIKeys(apiKeys.filter(k => k.id !== keyId));
    } catch (err: any) {
      alert(err.message || 'Failed to revoke API key');
    }
  };

  const handleCreateWebhook = async () => {
    if (!webhookUrl.trim()) {
      alert('Please enter a webhook URL');
      return;
    }
    if (webhookEvents.length === 0) {
      alert('Please select at least one event');
      return;
    }

    try {
      setCreateWebhookLoading(true);
      const response = await storePartnerAPI.createWebhook({
        webhook_url: webhookUrl,
        events: webhookEvents,
      });
      setWebhooks([response.webhook, ...webhooks]);
      setShowWebhookModal(false);
      setWebhookUrl('');
      setWebhookEvents([]);
    } catch (err: any) {
      alert(err.message || 'Failed to create webhook');
    } finally {
      setCreateWebhookLoading(false);
    }
  };

  const handleDeleteWebhook = async (webhookId: number) => {
    if (!window.confirm('Are you sure you want to delete this webhook?')) {
      return;
    }

    try {
      await storePartnerAPI.deleteWebhook(webhookId);
      setWebhooks(webhooks.filter(w => w.id !== webhookId));
    } catch (err: any) {
      alert(err.message || 'Failed to delete webhook');
    }
  };

  const toggleWebhookEvent = (event: string) => {
    if (webhookEvents.includes(event)) {
      setWebhookEvents(webhookEvents.filter(e => e !== event));
    } else {
      setWebhookEvents([...webhookEvents, event]);
    }
  };

  const copyToClipboard = (text: string) => {
    navigator.clipboard.writeText(text);
    alert('Copied to clipboard!');
  };

  if (!isAuthenticated) return null;

  return (
    <div style={styles.page}>
      <StoreHeader />

      <main style={styles.main}>
        <div style={styles.headerSection}>
          <h1 style={styles.pageTitle}>POS Integration</h1>
          <p style={styles.subtitle}>
            Connect your POS system to automatically sync purchases, track loyalty, and update inventory
          </p>
        </div>

        {/* Tabs */}
        <div style={styles.tabs}>
          <button
            style={{
              ...styles.tab,
              ...(activeTab === 'keys' ? styles.tabActive : {}),
            }}
            onClick={() => setActiveTab('keys')}
          >
            API Keys
          </button>
          <button
            style={{
              ...styles.tab,
              ...(activeTab === 'webhooks' ? styles.tabActive : {}),
            }}
            onClick={() => setActiveTab('webhooks')}
          >
            Webhooks
          </button>
          <button
            style={{
              ...styles.tab,
              ...(activeTab === 'logs' ? styles.tabActive : {}),
            }}
            onClick={() => setActiveTab('logs')}
          >
            Sync Log
          </button>
        </div>

        {loading ? (
          <div style={styles.loading}>Loading...</div>
        ) : error ? (
          <div style={styles.error}>{error}</div>
        ) : (
          <>
            {/* API Keys Tab */}
            {activeTab === 'keys' && (
              <div style={styles.section}>
                <div style={styles.sectionHeader}>
                  <h2 style={styles.sectionTitle}>API Keys</h2>
                  <button
                    onClick={() => setShowCreateKeyModal(true)}
                    style={styles.primaryBtn}
                  >
                    + Create API Key
                  </button>
                </div>

                <div style={styles.infoBox}>
                  <strong>Getting Started:</strong> Create an API key and use it in your POS system's
                  X-Partner-Key header to authenticate requests to our Partner API.
                </div>

                {apiKeys.length === 0 ? (
                  <div style={styles.emptyState}>
                    <p>No API keys yet. Create one to get started with POS integration.</p>
                  </div>
                ) : (
                  <div style={styles.keysList}>
                    {apiKeys.map(key => (
                      <div key={key.id} style={styles.keyCard}>
                        <div style={styles.keyHeader}>
                          <div style={styles.keyName}>{key.name}</div>
                          <span style={{
                            ...styles.keyBadge,
                            backgroundColor: key.key_type === 'live' ? '#e8f5e9' : '#fff3e0',
                            color: key.key_type === 'live' ? '#2e7d32' : '#ef6c00',
                          }}>
                            {key.key_type.toUpperCase()}
                          </span>
                          {!key.is_active && (
                            <span style={{ ...styles.keyBadge, backgroundColor: '#ffebee', color: '#c62828' }}>
                              REVOKED
                            </span>
                          )}
                        </div>
                        <div style={styles.keyValue}>
                          <code style={styles.keyCode}>{key.masked_key}</code>
                        </div>
                        <div style={styles.keyMeta}>
                          <span>Created: {new Date(key.created_at).toLocaleDateString()}</span>
                          {key.last_used_at && (
                            <span>Last used: {new Date(key.last_used_at).toLocaleString()}</span>
                          )}
                          <span>Requests: {key.request_count.toLocaleString()}</span>
                        </div>
                        {key.is_active && (
                          <button
                            onClick={() => handleRevokeKey(key.id)}
                            style={styles.revokeBtn}
                          >
                            Revoke Key
                          </button>
                        )}
                      </div>
                    ))}
                  </div>
                )}

                {/* API Documentation Link */}
                <div style={styles.docsSection}>
                  <h3 style={styles.docsTitle}>API Endpoints</h3>
                  <div style={styles.endpointsList}>
                    <div style={styles.endpoint}>
                      <code style={styles.endpointMethod}>GET</code>
                      <code style={styles.endpointPath}>/partner/v1/customer/{'{phone_or_member_id}'}/'</code>
                      <span style={styles.endpointDesc}>Look up customer by phone or member ID</span>
                    </div>
                    <div style={styles.endpoint}>
                      <code style={styles.endpointMethod}>POST</code>
                      <code style={styles.endpointPath}>/partner/v1/purchase/</code>
                      <span style={styles.endpointDesc}>Submit a purchase for loyalty tracking</span>
                    </div>
                    <div style={styles.endpoint}>
                      <code style={styles.endpointMethod}>POST</code>
                      <code style={styles.endpointPath}>/partner/v1/inventory/</code>
                      <span style={styles.endpointDesc}>Update store inventory levels</span>
                    </div>
                    <div style={styles.endpoint}>
                      <code style={styles.endpointMethod}>GET</code>
                      <code style={styles.endpointPath}>/partner/v1/products/</code>
                      <span style={styles.endpointDesc}>Get product catalog</span>
                    </div>
                    <div style={styles.endpoint}>
                      <code style={styles.endpointMethod}>POST</code>
                      <code style={styles.endpointPath}>/partner/v1/link-member/</code>
                      <span style={styles.endpointDesc}>Link store member ID to Bridge Club</span>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* Webhooks Tab */}
            {activeTab === 'webhooks' && (
              <div style={styles.section}>
                <div style={styles.sectionHeader}>
                  <h2 style={styles.sectionTitle}>Webhooks</h2>
                  <button
                    onClick={() => setShowWebhookModal(true)}
                    style={styles.primaryBtn}
                  >
                    + Add Webhook
                  </button>
                </div>

                <div style={styles.infoBox}>
                  Webhooks notify your system when events occur, such as when a customer earns a
                  reward or when bulk bin inventory is low.
                </div>

                {webhooks.length === 0 ? (
                  <div style={styles.emptyState}>
                    <p>No webhooks configured. Add one to receive real-time notifications.</p>
                  </div>
                ) : (
                  <div style={styles.webhooksList}>
                    {webhooks.map(webhook => (
                      <div key={webhook.id} style={styles.webhookCard}>
                        <div style={styles.webhookHeader}>
                          <code style={styles.webhookUrl}>{webhook.webhook_url}</code>
                          <span style={{
                            ...styles.keyBadge,
                            backgroundColor: webhook.is_active ? '#e8f5e9' : '#ffebee',
                            color: webhook.is_active ? '#2e7d32' : '#c62828',
                          }}>
                            {webhook.is_active ? 'ACTIVE' : 'PAUSED'}
                          </span>
                        </div>
                        <div style={styles.webhookEvents}>
                          {webhook.events.map(event => (
                            <span key={event} style={styles.eventTag}>{event}</span>
                          ))}
                        </div>
                        <div style={styles.webhookMeta}>
                          {webhook.last_triggered_at && (
                            <span>Last triggered: {new Date(webhook.last_triggered_at).toLocaleString()}</span>
                          )}
                          {webhook.failure_count > 0 && (
                            <span style={{ color: '#c62828' }}>Failures: {webhook.failure_count}</span>
                          )}
                        </div>
                        <button
                          onClick={() => handleDeleteWebhook(webhook.id)}
                          style={styles.revokeBtn}
                        >
                          Delete
                        </button>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}

            {/* Sync Log Tab */}
            {activeTab === 'logs' && (
              <div style={styles.section}>
                <h2 style={styles.sectionTitle}>Recent Purchase Syncs</h2>

                {purchaseLogs.length === 0 ? (
                  <div style={styles.emptyState}>
                    <p>No purchase syncs yet. Purchases submitted via the API will appear here.</p>
                  </div>
                ) : (
                  <div style={styles.tableContainer}>
                    <table style={styles.table}>
                      <thead>
                        <tr>
                          <th style={styles.th}>Date</th>
                          <th style={styles.th}>Transaction ID</th>
                          <th style={styles.th}>Member</th>
                          <th style={styles.th}>Total</th>
                          <th style={styles.th}>Loyalty</th>
                          <th style={styles.th}>Status</th>
                        </tr>
                      </thead>
                      <tbody>
                        {purchaseLogs.map(log => (
                          <tr key={log.id} style={styles.tr}>
                            <td style={styles.td}>
                              {new Date(log.purchase_date).toLocaleDateString()}
                            </td>
                            <td style={styles.td}>
                              <code style={styles.txId}>{log.partner_transaction_id}</code>
                            </td>
                            <td style={styles.td}>{log.member_name || '—'}</td>
                            <td style={styles.td}>${log.total}</td>
                            <td style={styles.td}>
                              {log.loyalty_credited > 0 && (
                                <span style={styles.loyaltyBadge}>+{log.loyalty_credited}</span>
                              )}
                              {log.free_bag_earned && (
                                <span style={styles.rewardBadge}>Free Bag!</span>
                              )}
                            </td>
                            <td style={styles.td}>
                              <span style={{
                                ...styles.statusBadge,
                                backgroundColor: log.status === 'processed' ? '#e8f5e9' :
                                                 log.status === 'failed' ? '#ffebee' : '#fff3e0',
                                color: log.status === 'processed' ? '#2e7d32' :
                                       log.status === 'failed' ? '#c62828' : '#ef6c00',
                              }}>
                                {log.status}
                              </span>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>
            )}
          </>
        )}

        {/* Create API Key Modal */}
        {showCreateKeyModal && (
          <div style={styles.modalOverlay}>
            <div style={styles.modal}>
              <div style={styles.modalHeader}>
                <h2 style={{ margin: 0 }}>Create API Key</h2>
                <button
                  onClick={() => {
                    setShowCreateKeyModal(false);
                    setCreatedKey(null);
                    setNewKeyName('');
                  }}
                  style={styles.closeBtn}
                >
                  &times;
                </button>
              </div>

              {createdKey ? (
                <div style={styles.keyCreatedSection}>
                  <div style={styles.successIcon}>✓</div>
                  <p style={styles.keyCreatedText}>
                    Your API key has been created. <strong>Copy it now</strong> - it won't be shown again!
                  </p>
                  <div style={styles.createdKeyBox}>
                    <code style={styles.createdKeyCode}>{createdKey}</code>
                    <button
                      onClick={() => copyToClipboard(createdKey)}
                      style={styles.copyBtn}
                    >
                      Copy
                    </button>
                  </div>
                  <button
                    onClick={() => {
                      setShowCreateKeyModal(false);
                      setCreatedKey(null);
                    }}
                    style={{ ...styles.primaryBtn, marginTop: '1rem' }}
                  >
                    Done
                  </button>
                </div>
              ) : (
                <>
                  <div style={styles.formGroup}>
                    <label style={styles.label}>Key Name</label>
                    <input
                      type="text"
                      value={newKeyName}
                      onChange={(e) => setNewKeyName(e.target.value)}
                      placeholder="e.g., Main POS System"
                      style={styles.input}
                    />
                  </div>

                  <div style={styles.formGroup}>
                    <label style={styles.label}>Key Type</label>
                    <div style={styles.radioGroup}>
                      <label style={styles.radioLabel}>
                        <input
                          type="radio"
                          value="test"
                          checked={newKeyType === 'test'}
                          onChange={() => setNewKeyType('test')}
                        />
                        <span style={styles.radioText}>
                          <strong>Test</strong> - For development and testing
                        </span>
                      </label>
                      <label style={styles.radioLabel}>
                        <input
                          type="radio"
                          value="live"
                          checked={newKeyType === 'live'}
                          onChange={() => setNewKeyType('live')}
                        />
                        <span style={styles.radioText}>
                          <strong>Live</strong> - For production use
                        </span>
                      </label>
                    </div>
                  </div>

                  <div style={styles.modalFooter}>
                    <button
                      onClick={() => setShowCreateKeyModal(false)}
                      style={styles.cancelBtn}
                    >
                      Cancel
                    </button>
                    <button
                      onClick={handleCreateKey}
                      disabled={createKeyLoading || !newKeyName.trim()}
                      style={{
                        ...styles.primaryBtn,
                        opacity: createKeyLoading || !newKeyName.trim() ? 0.6 : 1,
                      }}
                    >
                      {createKeyLoading ? 'Creating...' : 'Create Key'}
                    </button>
                  </div>
                </>
              )}
            </div>
          </div>
        )}

        {/* Create Webhook Modal */}
        {showWebhookModal && (
          <div style={styles.modalOverlay}>
            <div style={styles.modal}>
              <div style={styles.modalHeader}>
                <h2 style={{ margin: 0 }}>Add Webhook</h2>
                <button
                  onClick={() => {
                    setShowWebhookModal(false);
                    setWebhookUrl('');
                    setWebhookEvents([]);
                  }}
                  style={styles.closeBtn}
                >
                  &times;
                </button>
              </div>

              <div style={styles.formGroup}>
                <label style={styles.label}>Webhook URL</label>
                <input
                  type="url"
                  value={webhookUrl}
                  onChange={(e) => setWebhookUrl(e.target.value)}
                  placeholder="https://your-pos-system.com/webhook"
                  style={styles.input}
                />
              </div>

              <div style={styles.formGroup}>
                <label style={styles.label}>Events to Subscribe</label>
                <div style={styles.eventsList}>
                  {WEBHOOK_EVENTS.map(event => (
                    <label key={event.value} style={styles.eventCheckbox}>
                      <input
                        type="checkbox"
                        checked={webhookEvents.includes(event.value)}
                        onChange={() => toggleWebhookEvent(event.value)}
                      />
                      <div style={styles.eventInfo}>
                        <strong>{event.label}</strong>
                        <span style={styles.eventDesc}>{event.description}</span>
                      </div>
                    </label>
                  ))}
                </div>
              </div>

              <div style={styles.modalFooter}>
                <button
                  onClick={() => setShowWebhookModal(false)}
                  style={styles.cancelBtn}
                >
                  Cancel
                </button>
                <button
                  onClick={handleCreateWebhook}
                  disabled={createWebhookLoading || !webhookUrl.trim() || webhookEvents.length === 0}
                  style={{
                    ...styles.primaryBtn,
                    opacity: createWebhookLoading || !webhookUrl.trim() || webhookEvents.length === 0 ? 0.6 : 1,
                  }}
                >
                  {createWebhookLoading ? 'Creating...' : 'Add Webhook'}
                </button>
              </div>
            </div>
          </div>
        )}
      </main>
    </div>
  );
};

const styles: { [key: string]: React.CSSProperties } = {
  page: {
    minHeight: '100vh',
    backgroundColor: '#f5f5f5',
  },
  main: {
    maxWidth: '1200px',
    margin: '0 auto',
    padding: '1.5rem',
  },
  headerSection: {
    marginBottom: '1.5rem',
  },
  pageTitle: {
    fontSize: '1.75rem',
    fontWeight: 'bold',
    color: '#1a4d1a',
    margin: 0,
  },
  subtitle: {
    color: '#666',
    margin: '0.5rem 0 0 0',
  },
  tabs: {
    display: 'flex',
    gap: '0.5rem',
    marginBottom: '1.5rem',
    borderBottom: '2px solid #e0e0e0',
    paddingBottom: '0',
  },
  tab: {
    padding: '0.75rem 1.5rem',
    border: 'none',
    background: 'none',
    cursor: 'pointer',
    fontSize: '1rem',
    fontWeight: 500,
    color: '#666',
    borderBottom: '2px solid transparent',
    marginBottom: '-2px',
    transition: 'color 0.2s, border-color 0.2s',
  },
  tabActive: {
    color: '#1a4d1a',
    borderBottomColor: '#1a4d1a',
  },
  loading: {
    textAlign: 'center',
    padding: '3rem',
    color: '#666',
  },
  error: {
    backgroundColor: '#ffebee',
    color: '#c62828',
    padding: '1rem',
    borderRadius: '8px',
    textAlign: 'center',
  },
  section: {
    backgroundColor: '#fff',
    borderRadius: '12px',
    padding: '1.5rem',
    boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
  },
  sectionHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: '1rem',
  },
  sectionTitle: {
    fontSize: '1.25rem',
    fontWeight: 600,
    color: '#333',
    margin: 0,
  },
  primaryBtn: {
    backgroundColor: '#1a4d1a',
    color: '#fff',
    border: 'none',
    padding: '0.625rem 1rem',
    borderRadius: '6px',
    cursor: 'pointer',
    fontSize: '0.875rem',
    fontWeight: 500,
    minHeight: '44px',
  },
  infoBox: {
    backgroundColor: '#e3f2fd',
    color: '#1565c0',
    padding: '1rem',
    borderRadius: '8px',
    marginBottom: '1.5rem',
    fontSize: '0.875rem',
    lineHeight: 1.5,
  },
  emptyState: {
    textAlign: 'center',
    padding: '3rem',
    color: '#666',
  },
  keysList: {
    display: 'flex',
    flexDirection: 'column',
    gap: '1rem',
  },
  keyCard: {
    backgroundColor: '#fafafa',
    borderRadius: '8px',
    padding: '1rem',
    border: '1px solid #e0e0e0',
  },
  keyHeader: {
    display: 'flex',
    alignItems: 'center',
    gap: '0.75rem',
    marginBottom: '0.5rem',
  },
  keyName: {
    fontWeight: 600,
    color: '#333',
    fontSize: '1rem',
  },
  keyBadge: {
    padding: '0.25rem 0.5rem',
    borderRadius: '4px',
    fontSize: '0.75rem',
    fontWeight: 600,
  },
  keyValue: {
    marginBottom: '0.75rem',
  },
  keyCode: {
    backgroundColor: '#333',
    color: '#4caf50',
    padding: '0.5rem 0.75rem',
    borderRadius: '4px',
    fontFamily: 'monospace',
    fontSize: '0.875rem',
    display: 'inline-block',
  },
  keyMeta: {
    display: 'flex',
    gap: '1.5rem',
    color: '#666',
    fontSize: '0.75rem',
    marginBottom: '0.75rem',
    flexWrap: 'wrap',
  },
  revokeBtn: {
    backgroundColor: '#fff',
    color: '#c62828',
    border: '1px solid #c62828',
    padding: '0.5rem 0.75rem',
    borderRadius: '4px',
    cursor: 'pointer',
    fontSize: '0.75rem',
    fontWeight: 500,
  },
  docsSection: {
    marginTop: '2rem',
    paddingTop: '1.5rem',
    borderTop: '1px solid #e0e0e0',
  },
  docsTitle: {
    fontSize: '1rem',
    fontWeight: 600,
    color: '#333',
    marginBottom: '1rem',
  },
  endpointsList: {
    display: 'flex',
    flexDirection: 'column',
    gap: '0.5rem',
  },
  endpoint: {
    display: 'flex',
    alignItems: 'center',
    gap: '0.75rem',
    padding: '0.5rem',
    backgroundColor: '#fafafa',
    borderRadius: '4px',
    flexWrap: 'wrap',
  },
  endpointMethod: {
    backgroundColor: '#e8f5e9',
    color: '#2e7d32',
    padding: '0.25rem 0.5rem',
    borderRadius: '4px',
    fontSize: '0.75rem',
    fontWeight: 600,
    fontFamily: 'monospace',
  },
  endpointPath: {
    fontFamily: 'monospace',
    fontSize: '0.875rem',
    color: '#333',
  },
  endpointDesc: {
    fontSize: '0.75rem',
    color: '#666',
    marginLeft: 'auto',
  },
  webhooksList: {
    display: 'flex',
    flexDirection: 'column',
    gap: '1rem',
  },
  webhookCard: {
    backgroundColor: '#fafafa',
    borderRadius: '8px',
    padding: '1rem',
    border: '1px solid #e0e0e0',
  },
  webhookHeader: {
    display: 'flex',
    alignItems: 'center',
    gap: '0.75rem',
    marginBottom: '0.75rem',
    flexWrap: 'wrap',
  },
  webhookUrl: {
    fontFamily: 'monospace',
    fontSize: '0.875rem',
    color: '#333',
    wordBreak: 'break-all',
  },
  webhookEvents: {
    display: 'flex',
    gap: '0.5rem',
    flexWrap: 'wrap',
    marginBottom: '0.75rem',
  },
  eventTag: {
    backgroundColor: '#e3f2fd',
    color: '#1565c0',
    padding: '0.25rem 0.5rem',
    borderRadius: '4px',
    fontSize: '0.75rem',
  },
  webhookMeta: {
    display: 'flex',
    gap: '1.5rem',
    color: '#666',
    fontSize: '0.75rem',
    marginBottom: '0.75rem',
    flexWrap: 'wrap',
  },
  tableContainer: {
    overflowX: 'auto',
  },
  table: {
    width: '100%',
    borderCollapse: 'collapse',
  },
  th: {
    textAlign: 'left',
    padding: '0.75rem',
    borderBottom: '2px solid #e0e0e0',
    color: '#666',
    fontSize: '0.875rem',
    fontWeight: 600,
  },
  tr: {
    borderBottom: '1px solid #f0f0f0',
  },
  td: {
    padding: '0.75rem',
    fontSize: '0.875rem',
  },
  txId: {
    fontFamily: 'monospace',
    fontSize: '0.75rem',
    color: '#666',
  },
  loyaltyBadge: {
    backgroundColor: '#e8f5e9',
    color: '#2e7d32',
    padding: '0.25rem 0.5rem',
    borderRadius: '4px',
    fontSize: '0.75rem',
    fontWeight: 500,
    marginRight: '0.5rem',
  },
  rewardBadge: {
    backgroundColor: '#fff3e0',
    color: '#ef6c00',
    padding: '0.25rem 0.5rem',
    borderRadius: '4px',
    fontSize: '0.75rem',
    fontWeight: 500,
  },
  statusBadge: {
    padding: '0.25rem 0.5rem',
    borderRadius: '4px',
    fontSize: '0.75rem',
    fontWeight: 500,
  },
  modalOverlay: {
    position: 'fixed',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: 'rgba(0, 0, 0, 0.5)',
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    zIndex: 1000,
    padding: '1rem',
  },
  modal: {
    backgroundColor: '#fff',
    borderRadius: '12px',
    padding: '1.5rem',
    width: '100%',
    maxWidth: '500px',
    maxHeight: '90vh',
    overflow: 'auto',
  },
  modalHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: '1.5rem',
    borderBottom: '1px solid #e0e0e0',
    paddingBottom: '1rem',
  },
  closeBtn: {
    background: 'none',
    border: 'none',
    fontSize: '1.5rem',
    cursor: 'pointer',
    color: '#666',
    padding: '0.25rem 0.5rem',
  },
  formGroup: {
    marginBottom: '1.5rem',
  },
  label: {
    display: 'block',
    marginBottom: '0.5rem',
    fontWeight: 500,
    color: '#333',
  },
  input: {
    width: '100%',
    padding: '0.75rem',
    borderRadius: '6px',
    border: '1px solid #ccc',
    fontSize: '1rem',
    boxSizing: 'border-box',
  },
  radioGroup: {
    display: 'flex',
    flexDirection: 'column',
    gap: '0.75rem',
  },
  radioLabel: {
    display: 'flex',
    alignItems: 'flex-start',
    gap: '0.5rem',
    cursor: 'pointer',
  },
  radioText: {
    fontSize: '0.875rem',
    color: '#333',
  },
  eventsList: {
    display: 'flex',
    flexDirection: 'column',
    gap: '0.75rem',
  },
  eventCheckbox: {
    display: 'flex',
    alignItems: 'flex-start',
    gap: '0.75rem',
    padding: '0.75rem',
    backgroundColor: '#fafafa',
    borderRadius: '6px',
    cursor: 'pointer',
  },
  eventInfo: {
    display: 'flex',
    flexDirection: 'column',
    gap: '0.25rem',
  },
  eventDesc: {
    fontSize: '0.75rem',
    color: '#666',
  },
  modalFooter: {
    display: 'flex',
    justifyContent: 'flex-end',
    gap: '1rem',
    marginTop: '1.5rem',
    paddingTop: '1rem',
    borderTop: '1px solid #e0e0e0',
  },
  cancelBtn: {
    backgroundColor: '#e0e0e0',
    color: '#333',
    border: 'none',
    padding: '0.625rem 1rem',
    borderRadius: '6px',
    cursor: 'pointer',
    fontSize: '0.875rem',
    fontWeight: 500,
    minHeight: '44px',
  },
  keyCreatedSection: {
    textAlign: 'center',
    padding: '1rem 0',
  },
  successIcon: {
    width: '48px',
    height: '48px',
    borderRadius: '50%',
    backgroundColor: '#e8f5e9',
    color: '#2e7d32',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    fontSize: '1.5rem',
    margin: '0 auto 1rem',
  },
  keyCreatedText: {
    color: '#333',
    marginBottom: '1rem',
  },
  createdKeyBox: {
    backgroundColor: '#333',
    borderRadius: '8px',
    padding: '1rem',
    display: 'flex',
    alignItems: 'center',
    gap: '0.75rem',
    flexWrap: 'wrap',
  },
  createdKeyCode: {
    color: '#4caf50',
    fontFamily: 'monospace',
    fontSize: '0.875rem',
    wordBreak: 'break-all',
    flex: 1,
  },
  copyBtn: {
    backgroundColor: '#4caf50',
    color: '#fff',
    border: 'none',
    padding: '0.5rem 1rem',
    borderRadius: '4px',
    cursor: 'pointer',
    fontSize: '0.875rem',
    fontWeight: 500,
  },
};

export default StoreAPIIntegration;
